
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, User, Mail, Phone, Calendar, MessageSquare } from "lucide-react";
import { ContactFormData, FormType } from "../types/contact-widget";

interface ContactFormProps {
  formData: ContactFormData;
  formType: FormType;
  submitting: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const ContactForm = ({ formData, formType, submitting, onChange, onSubmit }: ContactFormProps) => {
  return (
    <form onSubmit={onSubmit} className="max-h-[70vh] overflow-y-auto p-6 space-y-6">
      <div className="space-y-4">
        <div className="relative">
          <Label htmlFor="name" className="inline-flex items-center gap-2 text-base font-medium text-gray-700">
            <User className="w-4 h-4 text-blue-500" />
            Your Name
          </Label>
          <div className="mt-1.5 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-focus-within:opacity-100 transition-opacity" />
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={onChange}
              required
              placeholder="Enter your full name"
              className="w-full relative z-10 border-blue-100 focus:border-blue-500 focus:ring-blue-500/20 placeholder:text-gray-400"
            />
          </div>
        </div>
        
        <div className="relative">
          <Label htmlFor="email" className="inline-flex items-center gap-2 text-base font-medium text-gray-700">
            <Mail className="w-4 h-4 text-blue-500" />
            Email Address
          </Label>
          <div className="mt-1.5 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-focus-within:opacity-100 transition-opacity" />
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={onChange}
              required
              placeholder="Enter your email address"
              className="w-full relative z-10 border-blue-100 focus:border-blue-500 focus:ring-blue-500/20 placeholder:text-gray-400"
            />
          </div>
        </div>
        
        <div className="relative">
          <Label htmlFor="phone" className="inline-flex items-center gap-2 text-base font-medium text-gray-700">
            <Phone className="w-4 h-4 text-blue-500" />
            Phone Number
            <span className="text-sm text-gray-500">(e.g., 09123456789)</span>
          </Label>
          <div className="mt-1.5 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-focus-within:opacity-100 transition-opacity" />
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={onChange}
              placeholder="Enter your Philippine mobile number"
              pattern="^(\+63|0)9\d{9}$"
              title="Please enter a valid Philippine mobile number (e.g., 09123456789 or +639123456789)"
              className="w-full relative z-10 border-blue-100 focus:border-blue-500 focus:ring-blue-500/20 placeholder:text-gray-400"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Format: 09123456789 or +639123456789</p>
        </div>

        {formType === 'booking' && (
          <div className="relative">
            <Label htmlFor="scheduledDate" className="inline-flex items-center gap-2 text-base font-medium text-gray-700">
              <Calendar className="w-4 h-4 text-blue-500" />
              Preferred Date & Time
            </Label>
            <div className="mt-1.5 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-focus-within:opacity-100 transition-opacity" />
              <Input
                id="scheduledDate"
                name="scheduledDate"
                type="datetime-local"
                value={formData.scheduledDate}
                onChange={onChange}
                required
                className="w-full relative z-10 border-blue-100 focus:border-blue-500 focus:ring-blue-500/20"
              />
            </div>
          </div>
        )}
        
        <div className="relative">
          <Label htmlFor="message" className="inline-flex items-center gap-2 text-base font-medium text-gray-700">
            <MessageSquare className="w-4 h-4 text-blue-500" />
            Your Message
          </Label>
          <div className="mt-1.5 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-focus-within:opacity-100 transition-opacity" />
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={onChange}
              required
              placeholder={formType === 'booking' ? 
                "What would you like to discuss in the meeting?" : 
                "How can we help you today?"}
              className="w-full relative z-10 min-h-[120px] border-blue-100 focus:border-blue-500 focus:ring-blue-500/20 placeholder:text-gray-400 resize-none"
            />
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Button
          type="submit"
          className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300"
          disabled={submitting}
        >
          {submitting ? (
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <MessageCircle className="h-5 w-5" />
              </motion.div>
              <span>Sending...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              <span>{formType === 'booking' ? 'Schedule Meeting' : 'Send Message'}</span>
            </div>
          )}
        </Button>
      </motion.div>

      <motion.p 
        className="text-center text-sm text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        We'll get back to you as soon as possible!
      </motion.p>
    </form>
  );
};
